import React, { useEffect, useState } from 'react';
import { User, Save, ClipboardList, Award, Pencil, MessageSquare, Mail, ArrowRight, Shield, Clock, CheckCircle, XCircle, Upload, AlertTriangle, Calendar } from 'lucide-react';
import { useApi } from './DashboardLayout';
import { DashboardBanner } from './Dashboard';
import { Link, useNavigate } from 'react-router-dom';

const STATUS_LABELS = { offen: 'Offen', in_arbeit: 'In Arbeit', erledigt: 'Erledigt' };
const PRIO_COLORS = { hoch: '#EF4444', mittel: '#F59E0B', niedrig: '#10B981' };

const PField = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label className="of-label">{label}</label>
    {children}
  </div>
);
const PTF = ({ label, value, onChange, type = 'text', placeholder }) => (
  <PField label={label}>
    <input className="of-input" type={type} value={value} onChange={onChange} placeholder={placeholder} />
  </PField>
);
const DF = ({ label, value }) => (
  <div style={{ marginBottom: 12 }}>
    <label className="of-label">{label}</label>
    <div className="of-value">{value || '-'}</div>
  </div>
);

export default function ProviderHomePage({ user, setUser }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [msg, setMsg] = useState('');
  const [offers, setOffers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [mails, setMails] = useState([]);
  const [latestNews, setLatestNews] = useState([]);
  const [banners, setBanners] = useState([]);
  const { apiFetch } = useApi();
  const navigate = useNavigate();

  // GBM state
  const [gbmStatus, setGbmStatus] = useState(null);
  const [showGbmForm, setShowGbmForm] = useState(false);
  const [gbmForm, setGbmForm] = useState({ participant_name: '', training_date: '', notes: '' });
  const [gbmFile, setGbmFile] = useState(null);
  const [gbmSubmitting, setGbmSubmitting] = useState(false);
  const [gbmMsg, setGbmMsg] = useState('');

  useEffect(() => {
    if (user) {
      setForm({
        ik_number: user.ik_number || '', verein: user.verein || '', street: user.street || '',
        postal_code: user.postal_code || '', city_location: user.city_location || '',
        phone: user.phone || '', contact_person: user.contact_person || '',
        email: user.email || '', website: user.website || '', dachverband: user.dachverband || '',
        billing_office_name: user.billing_office_name || '', billing_office_ik_number: user.billing_office_ik_number || ''
      });
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    const [offersR, statsR, newsR, bannerR, gbmR] = await Promise.all([
      apiFetch('/api/offers'),
      apiFetch('/api/dashboard/stats'),
      apiFetch('/api/news'),
      apiFetch('/api/settings/info-banner'),
      apiFetch('/api/gbm/status'),
    ]);
    const od = await offersR?.json();
    if (Array.isArray(od)) setOffers(od);
    const sd = await statsR?.json();
    if (sd) {
      setTickets(sd.recent_tickets || []);
      setMails(sd.recent_mails || []);
    }
    const nd = await newsR?.json();
    if (Array.isArray(nd)) setLatestNews(nd.slice(0, 3));
    try { const bd = await bannerR?.json(); if (bd?.banners) setBanners(bd.banners.filter(b => b.enabled_provider && b.content)); } catch {}
    try { const gd = await gbmR?.json(); if (gd) setGbmStatus(gd); } catch {}
  };

  const submitGbmApplication = async () => {
    if (!gbmFile) return;
    setGbmSubmitting(true); setGbmMsg('');
    const formData = new FormData();
    formData.append('certificate', gbmFile);
    formData.append('participant_name', gbmForm.participant_name);
    formData.append('training_date', gbmForm.training_date);
    formData.append('notes', gbmForm.notes);
    const r = await apiFetch('/api/gbm/apply', { method: 'POST', body: formData });
    if (r?.ok) {
      setGbmMsg('Antrag erfolgreich eingereicht!');
      setShowGbmForm(false);
      setGbmFile(null);
      setGbmForm({ participant_name: '', training_date: '', notes: '' });
      // Reload GBM status
      const gbmR = await apiFetch('/api/gbm/status');
      try { const gd = await gbmR?.json(); if (gd) setGbmStatus(gd); } catch {}
    } else {
      const err = await r?.json().catch(() => null);
      setGbmMsg(err?.detail || 'Fehler beim Einreichen');
    }
    setGbmSubmitting(false);
    setTimeout(() => setGbmMsg(''), 5000);
  };

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault(); setMsg('');
    const data = { ...form };
    if (data.website && !data.website.match(/^https?:\/\//)) {
      data.website = 'https://' + data.website;
      setForm(p => ({ ...p, website: data.website }));
    }
    const r = await apiFetch('/api/profile', { method: 'PUT', body: JSON.stringify(data) });
    if (r?.ok) {
      setMsg('Profil gespeichert');
      const mr = await apiFetch('/api/auth/me');
      const md = await mr?.json();
      if (md) setUser(md);
      setEditing(false);
    } else setMsg('Fehler beim Speichern');
    setTimeout(() => setMsg(''), 3000);
  };

  if (!user) return null;

  const certCount = offers.filter(o => o.certified === 1).length;

  return (
    <div data-testid="provider-home-page">
      <div className="page-header">
        <h2>Mein Bereich</h2>
        <p>{user.verein || 'Mein Verwaltungsbereich'}</p>
      </div>

      {msg && <div className={`design-feedback ${msg.includes('Fehler') ? 'error' : 'success'}`}>{msg}</div>}

      {/* === NEWS + INFO BANNER === */}
      <div style={{ display: 'grid', gridTemplateColumns: banners.length > 0 ? '1fr 320px' : '1fr', gap: 20, marginBottom: 24 }}>
        {/* LEFT: News Teaser */}
        {latestNews.length > 0 && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="of-section" style={{ marginBottom: 0 }}>Aktuelle Informationen</div>
              <Link to="/app/news" style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>Alle anzeigen <ArrowRight size={14} /></Link>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {latestNews.map(n => (
                <Link key={n.id} to={`/app/news?article=${n.id}`} style={{ textDecoration: 'none', display: 'block' }}>
                  <div className="of-card" style={{ padding: '12px 16px', transition: 'border-color 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}>
                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <div style={{ flexShrink: 0, width: 40, textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, lineHeight: 1, color: 'var(--primary)' }}>
                          {n.created_at?.substring(8, 10)}
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginTop: 2 }}>
                          {['Jan','Feb','Mär','Apr','Mai','Jun','Jul','Aug','Sep','Okt','Nov','Dez'][parseInt(n.created_at?.substring(5, 7) || '1') - 1]}
                        </div>
                      </div>
                      <div style={{ width: 3, background: 'var(--primary)', borderRadius: 2, alignSelf: 'stretch', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 2 }}>{n.title}</div>
                        {n.short_text && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                            {n.short_text.length > 100 ? n.short_text.substring(0, 100) + '...' : n.short_text}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* RIGHT: Info Banners */}
        {banners.length > 0 && (
          <div style={{ alignSelf: 'start', marginTop: 36, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {banners.map(b => <DashboardBanner key={b.id} banner={b} />)}
          </div>
        )}
      </div>

      {/* === PROFIL === */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div />
          <div style={{ display: 'flex', gap: 8 }}>
            {!editing && (
              <button className="btn btn-primary btn-sm" onClick={() => setEditing(true)} data-testid="provider-home-edit-btn">
                <Pencil size={14} /> Bearbeiten
              </button>
            )}
          </div>
        </div>

        {editing ? (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div className="of-card">
                <div className="of-section">Vereinsdaten</div>
                <PTF label="IK-Nummer" value={form.ik_number} onChange={e => set('ik_number', e.target.value)} />
                <PTF label="Verein" value={form.verein} onChange={e => set('verein', e.target.value)} />
                <PTF label="Dachverband" value={form.dachverband} onChange={e => set('dachverband', e.target.value)} />
              </div>
              <div className="of-card">
                <div className="of-section">Kontaktdaten</div>
                <PTF label="Ansprechpartner" value={form.contact_person} onChange={e => set('contact_person', e.target.value)} />
                <div className="of-2col">
                  <PTF label="E-Mail" value={form.email} onChange={e => set('email', e.target.value)} type="email" />
                  <PTF label="Telefon" value={form.phone} onChange={e => set('phone', e.target.value)} />
                </div>
                <PTF label="Website" value={form.website} onChange={e => set('website', e.target.value)} placeholder="www.beispiel.de" />
              </div>
            </div>
            <div className="of-card" style={{ marginBottom: 16 }}>
              <div className="of-section">Adresse</div>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '0 16px' }}>
                <PTF label="Straße" value={form.street} onChange={e => set('street', e.target.value)} />
                <PTF label="PLZ" value={form.postal_code} onChange={e => set('postal_code', e.target.value)} />
                <PTF label="Ort" value={form.city_location} onChange={e => set('city_location', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button className="btn btn-primary" onClick={handleSave} style={{ padding: '8px 24px' }} data-testid="provider-home-save-btn"><Save size={14} /> Aktualisieren</button>
              <button className="btn btn-secondary" onClick={() => setEditing(false)} style={{ padding: '8px 20px' }}>Abbrechen</button>
            </div>
          </>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div className="of-card">
              <div className="of-section">Kontaktdaten</div>
              <DF label="Verein" value={user.verein} />
              <div className="of-2col">
                <DF label="IK-Nummer" value={user.ik_number} />
                <DF label="Ansprechpartner" value={user.contact_person} />
              </div>
              <div className="of-2col">
                <DF label="E-Mail" value={user.email} />
                <DF label="Telefon" value={user.phone} />
              </div>
              <DF label="Website" value={user.website} />
            </div>
            <div className="of-card">
              <div className="of-section">Adresse & Abrechnung</div>
              <DF label="Straße" value={user.street} />
              <div className="of-2col">
                <DF label="PLZ" value={user.postal_code} />
                <DF label="Ort" value={user.city_location} />
              </div>
              <DF label="Dachverband" value={user.dachverband} />
              <DF label="Abrechnungsstelle" value={user.billing_office_name} />
            </div>
          </div>
        )}

      </div>

      {/* === GBM-BERECHTIGUNG === */}
      <div className="of-card" style={{ marginBottom: 24 }} data-testid="gbm-status-widget">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="of-section" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Shield size={16} style={{ color: 'var(--primary)' }} /> GBM-Berechtigung
          </div>
          {gbmStatus?.status === 'active' && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700,
              padding: '2px 10px', borderRadius: 12, background: '#D1FAE5', color: '#065F46',
            }}>
              <CheckCircle size={12} /> Aktiv bis {gbmStatus.permission?.valid_until ? new Date(gbmStatus.permission.valid_until).toLocaleDateString('de-DE') : '–'}
            </span>
          )}
        </div>

        {gbmMsg && (
          <div className={`design-feedback ${gbmMsg.includes('Fehler') || gbmMsg.includes('liegt bereits') ? 'error' : 'success'}`} style={{ marginBottom: 12 }}>
            {gbmMsg}
          </div>
        )}

        {/* Status: none */}
        {(!gbmStatus || gbmStatus.status === 'none') && !showGbmForm && (
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Shield size={28} style={{ opacity: 0.2, marginBottom: 8, color: 'var(--text-muted)' }} />
            <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-muted)' }}>
              Keine GBM-Berechtigung vorhanden
            </p>
            <button className="btn btn-primary btn-sm" onClick={() => setShowGbmForm(true)} data-testid="gbm-apply-btn">
              GBM-Antrag stellen
            </button>
          </div>
        )}

        {/* Status: pending */}
        {gbmStatus?.status === 'pending' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
            background: '#FFFBEB', borderRadius: 8, border: '1px solid #FDE68A',
          }}>
            <Clock size={20} style={{ color: '#D97706', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#92400E' }}>Antrag eingereicht</div>
              <div style={{ fontSize: 12, color: '#92400E' }}>
                Ihr GBM-Antrag vom {gbmStatus.latest_application?.submitted_at ? new Date(gbmStatus.latest_application.submitted_at).toLocaleDateString('de-DE') : '–'} wird geprüft.
              </div>
            </div>
          </div>
        )}

        {/* Status: active */}
        {gbmStatus?.status === 'active' && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
            background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0',
          }}>
            <CheckCircle size={20} style={{ color: '#16A34A', flexShrink: 0 }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#065F46' }}>GBM-Berechtigung aktiv</div>
              <div style={{ fontSize: 12, color: '#065F46' }}>
                Gültig bis: <strong>{gbmStatus.permission?.valid_until ? new Date(gbmStatus.permission.valid_until).toLocaleDateString('de-DE') : '–'}</strong>
                <span style={{ marginLeft: 8 }}>— GBM-Schulungsmaterialien sind im Downloadbereich freigeschaltet.</span>
              </div>
            </div>
          </div>
        )}

        {/* Status: expired */}
        {gbmStatus?.status === 'expired' && !showGbmForm && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
            background: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA',
          }}>
            <AlertTriangle size={20} style={{ color: '#DC2626', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#991B1B' }}>Berechtigung abgelaufen</div>
              <div style={{ fontSize: 12, color: '#991B1B' }}>
                Ihre GBM-Berechtigung ist am {gbmStatus.permission?.valid_until ? new Date(gbmStatus.permission.valid_until).toLocaleDateString('de-DE') : '–'} abgelaufen.
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowGbmForm(true)} data-testid="gbm-renew-btn">
              Erneut beantragen
            </button>
          </div>
        )}

        {/* Status: rejected */}
        {gbmStatus?.status === 'rejected' && !showGbmForm && (
          <div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
              background: '#FEF2F2', borderRadius: 8, border: '1px solid #FECACA', marginBottom: 12,
            }}>
              <XCircle size={20} style={{ color: '#DC2626', flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#991B1B' }}>Antrag abgelehnt</div>
                {gbmStatus.latest_application?.rejection_reason && (
                  <div style={{ fontSize: 12, color: '#991B1B', marginTop: 2 }}>
                    Begründung: {gbmStatus.latest_application.rejection_reason}
                  </div>
                )}
              </div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => setShowGbmForm(true)} data-testid="gbm-retry-btn">
              Erneut beantragen
            </button>
          </div>
        )}

        {/* Application form */}
        {showGbmForm && (
          <div style={{
            padding: '16px', background: 'var(--bg-secondary, #f9fafb)', borderRadius: 8,
            border: '1px solid var(--border)', marginTop: 8,
          }} data-testid="gbm-application-form">
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>GBM-Antrag stellen</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <PField label="Name des Schulungsteilnehmers *">
                <input className="of-input" value={gbmForm.participant_name}
                  onChange={e => setGbmForm(p => ({ ...p, participant_name: e.target.value }))}
                  placeholder="Vor- und Nachname" data-testid="gbm-participant-name" />
              </PField>
              <PField label="Schulungsdatum *">
                <input className="of-input" type="date" value={gbmForm.training_date}
                  onChange={e => setGbmForm(p => ({ ...p, training_date: e.target.value }))}
                  data-testid="gbm-training-date" />
              </PField>
            </div>
            <PField label="Zertifikat hochladen *">
              <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setGbmFile(e.target.files?.[0])}
                className="of-input" style={{ padding: 6 }} data-testid="gbm-certificate-input" />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>PDF, JPG oder PNG — Zertifikat der BGPR-Akademie</div>
            </PField>
            <PField label="Anmerkung (optional)">
              <textarea className="of-input" rows={2} value={gbmForm.notes}
                onChange={e => setGbmForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Zusätzliche Hinweise..." data-testid="gbm-notes" />
            </PField>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="btn btn-primary btn-sm" onClick={submitGbmApplication}
                disabled={gbmSubmitting || !gbmFile || !gbmForm.participant_name || !gbmForm.training_date}
                data-testid="gbm-submit-btn">
                {gbmSubmitting ? 'Wird eingereicht...' : 'Antrag einreichen'}
              </button>
              <button className="btn btn-secondary btn-sm"
                onClick={() => { setShowGbmForm(false); setGbmFile(null); }}>Abbrechen</button>
            </div>
          </div>
        )}
      </div>

      {/* === ANGEBOTE === */}
      <div className="of-card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div className="of-section" style={{ marginBottom: 0 }}>
            Meine Angebote ({offers.length})
            {certCount > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: '#10B981', marginLeft: 8 }}>– {certCount} zertifiziert</span>}
          </div>
          <Link to="/app/offers/new" style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none' }} data-testid="provider-home-add-offer">
            + Neues Angebot
          </Link>
        </div>
        {offers.length > 0 ? (
          <div className="table-wrap" style={{ margin: '0 -20px -20px' }}>
            <table data-testid="provider-home-offers">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Nr.</th>
                  <th>Angebot</th>
                  <th>Veranstaltungsort</th>
                  <th>Zertifizierung</th>
                  <th style={{ width: 80 }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {offers.map(o => (
                  <tr key={o.id} data-testid={`provider-home-offer-${o.id}`}
                    onClick={() => navigate(`/app/offers/${o.id}/edit`)}
                    style={{ cursor: 'pointer' }}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 13 }}>{o.id}</td>
                    <td style={{ fontWeight: 500 }}>{o.angebot || o.offer_name || '-'}</td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                      {[o.veranstaltungsort_name, o.veranstaltungsort_strasse, o.veranstaltungsort_plz, o.veranstaltungsort_ort].filter(Boolean).join(', ') || '-'}
                    </td>
                    <td>
                      {o.deregistration_date ? (
                        <span className="badge" style={{ background: '#FEF3C7', color: '#92400E', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          Abmeldung {o.deregistration_date.split('-').reverse().join('.')}
                        </span>
                      ) : o.certified === 1 ? (
                        <span className="badge badge-success" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                          <Award size={11} /> Zertifiziert
                          {o.cert_valid_until && <span style={{ opacity: 0.7, fontSize: 11, marginLeft: 4 }}>bis {o.cert_valid_until?.substring(0, 10)}</span>}
                        </span>
                      ) : (
                        <span className="badge badge-warning">Nicht zertifiziert</span>
                      )}
                    </td>
                    <td>
                      <Link to={`/app/offers/${o.id}/edit`} className="btn-icon edit" title="Bearbeiten" data-testid={`provider-home-offer-edit-${o.id}`}>
                        <Pencil size={14} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
            <ClipboardList size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
            <p style={{ margin: 0, fontSize: 13 }}>Keine Angebote vorhanden</p>
          </div>
        )}
      </div>

      {/* === TICKETS & E-MAILS === */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div className="of-card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="of-section" style={{ marginBottom: 0 }}>Meine Tickets</div>
            {tickets.length > 0 && <Link to="/app/tickets" style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>Alle <ArrowRight size={14} /></Link>}
          </div>
          {tickets.length > 0 ? (
            <div style={{ margin: '0 -20px -20px' }}>
              <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                  {tickets.map((t, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>#{t.id} {t.title}</div>
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 12, color: PRIO_COLORS[t.priority] || '#6b7280', background: 'rgba(0,0,0,0.04)' }}>
                          {(t.priority || '').toUpperCase()}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 12, marginLeft: 4, background: '#f3f4f6', color: '#374151' }}>
                          {STATUS_LABELS[t.status] || t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
              <MessageSquare size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ margin: 0, fontSize: 13 }}>Keine Tickets vorhanden</p>
            </div>
          )}
        </div>

        {/* === E-MAILS === */}
        <div className="of-card">
          <div className="of-section" style={{ marginBottom: 12 }}>Erhaltene E-Mails</div>
          {mails.length > 0 ? (
            <div style={{ margin: '0 -20px -20px' }}>
              <table style={{ width: '100%', fontSize: 13 }}>
                <tbody>
                  {mails.map((m, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '10px 16px' }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{m.subject?.substring(0, 50)}{m.subject?.length > 50 ? '...' : ''}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {m.sent_at ? new Date(m.sent_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-muted)' }}>
              <Mail size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
              <p style={{ margin: 0, fontSize: 13 }}>Keine E-Mails erhalten</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
